import { motion } from "framer-motion";
import { MessageCircle, ShieldCheck } from "lucide-react";
import { WaChatFrame, WaBubbleReceived } from "./WhatsAppMockup";

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1 } }),
};

const PROGRESS = [
  { label: "Héroes", icon: "🦸", pct: 96 },
  { label: "Mascotas", icon: "🐾", pct: 90 },
  { label: "Tropas", icon: "⚔️", pct: 98 },
  { label: "Hechizos", icon: "🔮", pct: 94 },
  { label: "Equipamientos", icon: "🛡️", pct: 88 },
];

function ProgressBars() {
  return (
    <div className="mt-2 space-y-1.5" data-testid="join-log-progress-bars">
      {PROGRESS.map((p) => (
        <div key={p.label} className="flex items-center gap-2 text-[11px] sm:text-xs">
          <span className="w-[92px] sm:w-[104px] shrink-0 text-wa-subtext">{p.icon} {p.label}</span>
          <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-gold to-emerald-400" style={{ width: `${p.pct}%` }} />
          </div>
          <span className="w-9 text-right font-mono text-wa-text">{p.pct}%</span>
        </div>
      ))}
    </div>
  );
}

function JoinLogCard() {
  return (
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} custom={0} variants={fade}
      className="card-surface p-4 sm:p-5" data-testid="log-ingreso-salida-card">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="text-2xl">📥</span>
        <div>
          <h3 className="font-heading font-semibold text-base sm:text-lg">Log de Ingreso / Salida de Miembros</h3>
          <p className="text-xs text-muted-foreground">Ficha militar completa al instante, sin abrir el juego</p>
        </div>
      </div>
      <WaChatFrame contactName="Imperio Hispano - Oficial" subtitle="ᵁᴺᴿ⚝ WARDEN · en línea" className="mt-4">
        <WaBubbleReceived
          testid="wa-bubble-join-header"
          text={
            "👋 **Nuevo miembro en Imperio Hispano**\n━━━━━━━━━━━━━━━━━━━━━━\n👤 **KingArthur_CoC**  `#92VY80QQ`\n🏛️ TH16   ⭐ Nivel 245\n🏆 Trofeos: 5,912 (récord 5,940)\n⚔️ Estrellas de guerra: 1,845\n🏅 Liga: Legend League"
          }
        />
        <div className="flex justify-start">
          <div className="relative bubble-tail-received bg-wa-received text-wa-text rounded-lg rounded-tl-none px-3.5 py-3 max-w-[92%] text-xs sm:text-sm shadow-md">
            <div className="font-mono text-wa-text mb-1">📊 <strong>Progreso basado en la API:</strong> 96%</div>
            <ProgressBars />
            <div className="text-[10px] text-wa-subtext mt-2">📌 Basado en datos visibles de la API oficial de Supercell.</div>
            <div className="text-right text-[10px] text-white/40 mt-1.5">14:32 ✓✓</div>
          </div>
        </div>
      </WaChatFrame>
    </motion.div>
  );
}

function WarReminderCard() {
  return (
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} variants={fade}
      className="card-surface p-4 sm:p-5 relative overflow-hidden" data-testid="alertas-guerra-card">
      <div className="absolute inset-0 pointer-events-none border-2 border-gold/0 rounded-md" />
      <div className="flex items-center gap-2.5 mb-1">
        <span className="text-2xl">⚔️</span>
        <div>
          <h3 className="font-heading font-semibold text-base sm:text-lg">Alertas y Recordatorios de Guerra / CWL</h3>
          <p className="text-xs text-muted-foreground">Nadie vuelve a olvidar un ataque, con tiempo restante en vivo</p>
        </div>
      </div>
      <WaChatFrame contactName="Imperio Hispano - Oficial" subtitle="ᵁᴺᴿ⚝ WARDEN · en línea" className="mt-4">
        <div className="flex justify-start">
          <div className="relative bubble-tail-received rounded-lg rounded-tl-none px-3.5 py-3 max-w-[94%] text-xs sm:text-sm shadow-md border-l-2 border-crimson bg-wa-received">
            <div className="font-mono">
              <div className="text-gold font-bold mb-1">⏳ ¡La Guerra termina en ~2h!</div>
              <div className="border-t border-wa-subtext/20 my-1.5" />
              <div className="text-wa-text">⚔️ Imperio Hispano vs Dragones del Norte</div>
              <div className="mt-2 text-crimson font-bold">🔴 Ataques pendientes (4):</div>
              <div className="mt-1 flex flex-col gap-1">
                {[["Carlos_CoC", "TH15", 2], ["ShadowNinja", "TH14", 1], ["Valquiria_Top", "TH16", 1]].map(([n, th, f]) => (
                  <div key={n} className="flex items-center gap-1.5 text-wa-text">
                    <span className="text-[11px] bg-wa-accent/15 text-wa-accent px-1.5 py-0.5 rounded-full font-semibold">@{n}</span>
                    <span className="text-wa-subtext text-[11px]">({th}) — faltan {f}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-wa-text">¡No dejen ataques sin usar! 🗡️</div>
            </div>
            <div className="text-right text-[10px] text-white/40 mt-1.5">14:32</div>
          </div>
        </div>
        <WaBubbleReceived
          testid="wa-bubble-war-started"
          text={"⚔️ **¡Comenzó la Guerra!**\n━━━━━━━━━━━━━━━━━━━━━━\n🟢 Imperio Hispano\n🆚\n🔴 Dragones del Norte\n\n👥 15v15\n⏳ Termina en: 23h 58m\n\n¡A por la victoria! 🔥"}
        />
      </WaChatFrame>
    </motion.div>
  );
}

export default function AutomatedAlerts() {
  return (
    <section id="alertas" className="max-w-7xl mx-auto px-5 md:px-8 py-20" data-testid="whatsapp-alerts-section">
      <div className="text-center max-w-2xl mx-auto mb-4">
        <span className="overline text-emerald-400">Avisos automáticos en tiempo real</span>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">Tu clan avisado, sin que nadie tenga que preguntar</h2>
        <p className="text-muted-foreground mt-3">
          Alertas inteligentes que trabajan en segundo plano dentro del chat de WhatsApp de tu clan — el mismo lugar donde tu comunidad ya vive todos los días.
        </p>
      </div>
      <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-2 mx-auto max-w-fit mb-12">
        <ShieldCheck size={15} /> 100% dentro de WhatsApp — sin apps nuevas, sin Discord, sin abrir una PC.
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <JoinLogCard />
        <WarReminderCard />
      </div>
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={2}
        className="mt-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
        <MessageCircle size={14} className="text-gold" /> Ejemplo ilustrativo con datos ficticios. El bot real usa los datos oficiales en vivo de tu clan.
      </motion.div>
    </section>
  );
}
